import { useEffect } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Chip, Typography } from '@mui/material';
import { ColumnHeader, SimpleTable } from './components/SimpleTable';
import {
  FiltersBar,
  SelectFreeOffers,
  SelectConvertedTo,
  SelectConversionStatus,
} from './CrmFiltersBar';
import TOOLTIPS from './tooltips';
import { trackEvent } from '@services/tracking';

export type FreeUserDto = {
  id: number;
  name: string;
  email: string;
  isEmailVerified: boolean;
  instagramProfile: string;
  marketingConsent: boolean;
  freeOtpOffers: Array<{ id: number; name: string }>;
  claimedAt: string;
  convertedToProduct: { name: string; productType: string } | null;
  convertedAt: string | null;
  allPurchasedProducts: Array<{ id: number; name: string }>;
};

const columns = [
  {
    title: 'Name',
    field: 'name',
  },
  {
    title: <ColumnHeader label="Email" title={TOOLTIPS.email} />,
    render: (row: FreeUserDto) => (
      <div className="flex items-center">
        {row.isEmailVerified && row.marketingConsent ? (
          <Icon icon={faBadgeCheck} className="text-primary mr-2" />
        ) : null}
        <span className="select-all">
          {row.marketingConsent ? row.email : row.email?.replace(/./g, '*')}
        </span>
      </div>
    ),
  },
  {
    title: 'Instagram',
    render: (row: FreeUserDto) => (
      <div>
        {row.marketingConsent
          ? row.instagramProfile
          : row.instagramProfile?.replace(/./g, '*')}
      </div>
    ),
  },
  {
    title: <ColumnHeader label="Marketing" title={TOOLTIPS.marketing} />,
    render: (row: FreeUserDto) => (
      <div>{row.marketingConsent ? 'Yes' : 'No'}</div>
    ),
  },
  {
    title: 'Free offers redeemed',
    render: (row: FreeUserDto) => (
      <div className="flex gap-4">
        {row.freeOtpOffers.map((offer) => (
          <Chip
            key={offer.id}
            label={offer.name}
            className="border border-solid border-[#ECEDF2] bg-white text-[#151E34] h-[26px] text-sm"
          />
        ))}
      </div>
    ),
  },
  {
    title: 'Redemption date',
    render: (row: FreeUserDto) => (
      <div>
        {row.claimedAt ? new Date(row.claimedAt).toLocaleDateString() : '-'}
      </div>
    ),
  },
  {
    title: '1st paid offer',
    render: (row: FreeUserDto) => (
      <div>{row.convertedToProduct?.name ?? '-'}</div>
    ),
  },
  {
    title: 'Converted at',
    render: (row: FreeUserDto) => (
      <div>
        {row.convertedAt ? new Date(row.convertedAt).toLocaleDateString() : '-'}
      </div>
    ),
  },
  {
    title: 'All products purchased',
    render: (row: FreeUserDto) => (
      <div className="flex gap-4">
        {row.allPurchasedProducts.map((product) => (
          <Chip
            key={product.id}
            label={product.name}
            className="border border-solid border-[#ECEDF2] bg-white text-[#151E34] h-[26px] text-sm"
          />
        ))}
      </div>
    ),
  },
].map((c) => ({ ...c, sorting: false }));

export type FreeOffersTableProps = {
  data: FreeUserDto[];
  totalResults: number;
};
export function FreeOffersTable({
  data,
  totalResults,
  ...props
}: FreeOffersTableProps) {
  useEffect(() => {
    trackEvent({
      type: 'Viewed Free Offers Table',
      payload: {},
    });
  }, []);

  return (
    <div className="max-w-full">
      <FiltersBar totalResults={totalResults}>
        <SelectFreeOffers />
        <SelectConvertedTo />
        <SelectConversionStatus />
      </FiltersBar>
      <div className="my-3 text-sm text-[#8A8E99]">
        <Typography>
          Some user details may not appear in this list as they've opted to have
          their data hidden per GDPR requirements. This data is solely intended
          for marketing purposes.
        </Typography>
      </div>
      <SimpleTable {...props} columns={columns} data={data} />
    </div>
  );
}
